/**
 * Manual Submission Hooks
 *
 * React Query hooks for manual NIB entry operations.
 * Note: Submission creation only needs period - employer info comes from BusinessProfile.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { manualEntryApi } from '@/services/api/manualEntryApi';
import {
  ManualSubmission,
  CreateSubmissionRequest,
  AddEmployeeRequest,
  UpdateEmployeeRequest,
  UpdatePeriodRequest,
  BulkAddEmployeesRequest,
  EmployeeCalculation,
} from '@/types/nib/manualEntry';

// Query keys
export const submissionKeys = {
  all: ['manual-submissions'] as const,
  access: () => [...submissionKeys.all, 'access'] as const,
  limits: () => [...submissionKeys.all, 'limits'] as const,
  lists: () => [...submissionKeys.all, 'list'] as const,
  list: (filters: { includeCompleted?: boolean }) =>
    [...submissionKeys.lists(), filters] as const,
  details: () => [...submissionKeys.all, 'detail'] as const,
  detail: (id: string) => [...submissionKeys.details(), id] as const,
};

// =============================================================================
// ACCESS & LIMITS QUERIES
// =============================================================================

/**
 * Check user access to manual entry and get tier limits.
 * Use this on app load to verify access and show tier badge.
 */
export function useAccessCheck() {
  return useQuery({
    queryKey: submissionKeys.access(),
    queryFn: () => manualEntryApi.checkAccess(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get detailed tier limits and current usage.
 * Use this to show usage indicators and upgrade prompts.
 */
export function useLimits() {
  return useQuery({
    queryKey: submissionKeys.limits(),
    queryFn: () => manualEntryApi.getLimits(),
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
}

// =============================================================================
// SUBMISSION QUERIES
// =============================================================================

export function useSubmissionList(includeCompleted = true) {
  return useQuery({
    queryKey: submissionKeys.list({ includeCompleted }),
    queryFn: () => manualEntryApi.listSubmissions(includeCompleted),
  });
}

export function useSubmission(submissionId: string | undefined) {
  return useQuery({
    queryKey: submissionKeys.detail(submissionId!),
    queryFn: () => manualEntryApi.getSubmission(submissionId!),
    enabled: !!submissionId,
    // Refetch to get updated calculations
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// SUBMISSION MUTATIONS
// =============================================================================

export function useCreateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSubmissionRequest) =>
      manualEntryApi.createSubmission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submissionId: string) =>
      manualEntryApi.deleteSubmission(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

export function useUpdatePeriod(submissionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePeriodRequest) =>
      manualEntryApi.updatePeriod(submissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: submissionKeys.detail(submissionId),
      });
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

// =============================================================================
// EMPLOYEE MUTATIONS
// =============================================================================

export function useAddEmployee(submissionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddEmployeeRequest) =>
      manualEntryApi.addEmployee(submissionId, data),
    onSuccess: () => {
      // Refetch submission to get updated totals
      queryClient.invalidateQueries({
        queryKey: submissionKeys.detail(submissionId),
      });
    },
  });
}

export function useUpdateEmployee(submissionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      employeeId,
      data,
    }: {
      employeeId: string;
      data: UpdateEmployeeRequest;
    }) => manualEntryApi.updateEmployee(submissionId, employeeId, data),
    onSuccess: () => {
      // Refetch submission to get updated totals
      queryClient.invalidateQueries({
        queryKey: submissionKeys.detail(submissionId),
      });
    },
  });
}

export function useRemoveEmployee(submissionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: string) =>
      manualEntryApi.removeEmployee(submissionId, employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: submissionKeys.detail(submissionId),
      });
    },
  });
}

/**
 * Bulk add employees to submission.
 * Maximum 100 employees per request.
 * Returns summary with success/failure counts.
 */
export function useBulkAddEmployees(submissionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkAddEmployeesRequest) =>
      manualEntryApi.bulkAddEmployees(submissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: submissionKeys.detail(submissionId),
      });
      queryClient.invalidateQueries({ queryKey: submissionKeys.limits() });
    },
  });
}

// =============================================================================
// GENERATION MUTATION
// =============================================================================

export function useGenerateC10(submissionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => manualEntryApi.generateC10(submissionId),
    onSuccess: () => {
      // Mark as no longer draft
      queryClient.invalidateQueries({
        queryKey: submissionKeys.detail(submissionId),
      });
      queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
    },
  });
}

/**
 * Download Excel template for bulk employee import.
 * Returns a Blob that can be used to trigger file download.
 */
export function useDownloadTemplate() {
  return useMutation({
    mutationFn: () => manualEntryApi.downloadTemplate(),
  });
}

// =============================================================================
// OPTIMISTIC UPDATE HELPERS
// =============================================================================

export function useOptimisticEmployeeAdd(submissionId: string) {
  const queryClient = useQueryClient();
  const mutation = useAddEmployee(submissionId);

  const addWithOptimistic = async (data: AddEmployeeRequest) => {
    // Optimistic update
    const previousData = queryClient.getQueryData<ManualSubmission>(
      submissionKeys.detail(submissionId)
    );

    if (previousData) {
      // Create placeholder employee
      const tempEmployee: EmployeeCalculation = {
        id: Date.now(),
        ni_number: data.ni_number,
        name: `${data.first_name.toUpperCase()} ${data.last_name.toUpperCase()}`,
        first_name: data.first_name.toUpperCase(),
        last_name: data.last_name.toUpperCase(),
        basic_wages: data.basic_wages,
        gratuity_wages: data.gratuity_wages || 0,
        weeks_worked: data.weeks_worked,
        pay_indicator: data.pay_indicator,
        category: data.category,
        truncated_wages: data.basic_wages,
        capped_wages: data.basic_wages,
        was_capped: false,
        cap_applied: null,
        employee_contribution: 0, // Will be calculated server-side
        employer_contribution: 0,
        gratuity_contribution: 0,
        total_contribution: 0,
        is_valid: true,
        warnings: ['Calculating...'],
      };

      queryClient.setQueryData<ManualSubmission>(
        submissionKeys.detail(submissionId),
        {
          ...previousData,
          employees: [...previousData.employees, tempEmployee],
        }
      );
    }

    try {
      return await mutation.mutateAsync(data);
    } catch (error) {
      // Rollback on error
      if (previousData) {
        queryClient.setQueryData(
          submissionKeys.detail(submissionId),
          previousData
        );
      }
      throw error;
    }
  };

  return { ...mutation, addWithOptimistic };
}
